import type { JSX } from 'react';

import type { UseFormRegisterReturn, FieldPath, FieldValues } from 'react-hook-form';

import NewSelect from '@/components/ui/NewSelect';
import appIcons from '@/constants/appIcons';
import useFetchFleetSizes from '@/features/fleetSizes/hooks/useFetchFleetSizes';
import type { FleetSize } from '@/features/fleetSizes/types/FleetSize';
import useI18n from '@/hooks/useI18n';

type FleetSizeDropdownProps<T extends FieldValues> = {
    fleetSize: FleetSize | null;
    setFleetSize: (fleetSize: FleetSize | null) => void;
    errorMessage?: string;
    label?: string;
    className?: string;
    register?: UseFormRegisterReturn<FieldPath<T>>;
};

export default function FleetSizeDropdown<T extends FieldValues>({
    fleetSize,
    setFleetSize,
    errorMessage,
    label,
    className,
    register,
}: FleetSizeDropdownProps<T>): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();

    const { fleetSizes, isLoading } = useFetchFleetSizes({ perPage: 100 });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <NewSelect
            {...register}
            label={label ?? ''}
            placeholder={t('Auth.Number_Vehicles_Placeholder')}
            prefixIcon={appIcons.outline.car}
            options={fleetSizes}
            isLoading={isLoading}
            value={fleetSize}
            optionIdKey="id"
            className={className}
            errorMessage={errorMessage}
            isClearable={false}
            isRequired
            onChange={setFleetSize}
        />
    );
}
